// src/pages/Dashboard/vendor-balance/components/VendorBalanceTable.jsx
import React, { useState, useEffect } from 'react';
import styles from './VendorBalanceTable.module.css';
import * as XLSX from 'xlsx';
import { vendorBalanceApi } from '../../../../api/vendor-balance/vendorBalanceApi';

const VendorBalanceTable = () => {
  const [allBalances, setAllBalances] = useState([]);
  const [filteredBalances, setFilteredBalances] = useState([]);
  const [displayBalances, setDisplayBalances] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAging, setSelectedAging] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const itemsPerPage = 20;

  const agingFilters = [
    { label: 'All', value: '' },
    { label: '0-30 Days', value: '0-30' },
    { label: '31-60 Days', value: '31-60' },
    { label: '61-90 Days', value: '61-90' },
    { label: '90+ Days', value: '90+' }
  ];

  useEffect(() => { fetchAllBalances(); }, []);

  useEffect(() => { applyFiltersAndPagination(); }, [allBalances, searchTerm, selectedAging, currentPage]);

  const fetchAllBalances = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await vendorBalanceApi.exportVendorBalances('');
      if (data && Array.isArray(data)) {
        setAllBalances(data);
        setCurrentPage(0);
      } else {
        setAllBalances([]);
        setError('Invalid data received from server');
      }
    } catch (err) {
      setError('Failed to load vendor balances');
      setAllBalances([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndPagination = () => {
    let filtered = [...allBalances];

    if (searchTerm && searchTerm.trim() !== '') {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(b =>
        b.invoiceNo?.toString().toLowerCase().includes(s) ||
        b.vendorCode?.toLowerCase().includes(s) ||
        b.vendorName?.toLowerCase().includes(s) ||
        b.contactPerson?.toLowerCase().includes(s) ||
        b.vendorRefNo?.toLowerCase().includes(s) ||
        b.country?.toLowerCase().includes(s) ||
        b.state?.toLowerCase().includes(s) ||
        b.region?.toLowerCase().includes(s) ||
        b.paymentTerms?.toLowerCase().includes(s) ||
        b.salesPerson?.toLowerCase().includes(s) ||
        b.masterSalesPerson?.toLowerCase().includes(s) ||
        b.balanceDue?.toString().includes(s) ||
        b.overdueDays?.toString().includes(s)
      );
    }

    if (selectedAging) filtered = filterByAging(filtered, selectedAging);

    setFilteredBalances(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));

    const start = currentPage * itemsPerPage;
    setDisplayBalances(filtered.slice(start, start + itemsPerPage));
  };

  const filterByAging = (data, aging) => {
    switch (aging) {
      case '0-30':  return data.filter(b => b.overdueDays >= 0 && b.overdueDays <= 30);
      case '31-60': return data.filter(b => b.overdueDays >= 31 && b.overdueDays <= 60);
      case '61-90': return data.filter(b => b.overdueDays >= 61 && b.overdueDays <= 90);
      case '90+':   return data.filter(b => b.overdueDays > 90);
      default:      return data;
    }
  };

  const handleExportExcel = () => {
    try {
      const dataToExport = filteredBalances.length > 0 ? filteredBalances : allBalances;
      const worksheet = XLSX.utils.json_to_sheet(dataToExport.map(b => ({
        'Invoice No': b.invoiceNo,
        'Invoice Date': b.invoiceDate,
        'Vendor Ref No': b.vendorRefNo,
        'Vendor Code': b.vendorCode,
        'Vendor Name': b.vendorName,
        'Contact Person': b.contactPerson,
        'Invoice Total': b.invoiceTotal,
        'Balance Due': b.balanceDue,
        'Country': b.country,
        'State': b.state,
        'Region': b.region,
        'Overdue Days': b.overdueDays,
        'Payment Terms': b.paymentTerms,
        'Supply Date': b.supplyDate,
        'Sales Person': b.salesPerson,
        'Master Sales Person': b.masterSalesPerson,
        'Payment Status': b.paymentStatus
      })));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendor Balances');
      XLSX.writeFile(workbook, 'vendor_balances.xlsx');
    } catch (err) {
      alert('Failed to export data');
    }
  };

  const formatCurrency = (amount) => '₹' + (amount || 0).toLocaleString('en-IN');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getOverdueBadgeClass = (days) => {
    if (days === 0) return styles.overdueBadge0;
    if (days <= 30) return styles.overdueBadge30;
    return styles.overdueBadge90;
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.pageTitle}>Vendor Balance Report</h2>
        {!loading && (
          <div className={styles.recordCount}>
            {filteredBalances.length === allBalances.length
              ? `${allBalances.length} total records`
              : `${filteredBalances.length} of ${allBalances.length} records`}
          </div>
        )}
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.searchRow}>
          <div className={styles.searchInputWrapper}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Search by invoice, vendor, code, region..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
              onKeyPress={(e) => e.key === 'Enter' && setCurrentPage(0)}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button onClick={() => { setSearchTerm(''); setCurrentPage(0); }} className={styles.clearSearchButton}>✕</button>
            )}
          </div>
          <button onClick={() => setCurrentPage(0)} disabled={loading} className={styles.searchButton}>Search</button>
        </div>

        <div className={styles.agingFilterRow}>
          <span className={styles.agingLabel}>Filter by Overdue Days:</span>
          <div className={styles.agingButtonGroup}>
            {agingFilters.map((f) => (
              <button
                key={f.value}
                className={`${styles.agingButton} ${selectedAging === f.value ? styles.agingButtonActive : ''}`}
                onClick={() => { setSelectedAging(f.value); setCurrentPage(0); }}
                disabled={loading}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.filterRow}>
          <div />
          <div className={styles.actionButtons}>
            <button onClick={() => { setSearchTerm(''); setSelectedAging(''); setCurrentPage(0); }} disabled={loading} className={styles.clearButton}>
              Clear All Filters
            </button>
            <button onClick={handleExportExcel} disabled={loading || allBalances.length === 0} className={styles.excelButton}>
              <span>📊</span> Export Excel
            </button>
          </div>
        </div>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading all vendor balance records...</div>
        </div>
      )}

      {!loading && (
        <div className={styles.tableContainer}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Invoice No.</th>
                  <th className={styles.th}>Invoice Date</th>
                  <th className={styles.th}>Vendor Ref No</th>
                  <th className={styles.th}>Vendor Code</th>
                  <th className={styles.th}>Vendor Name</th>
                  <th className={styles.th}>Contact Person</th>
                  <th className={styles.th}>Invoice Total</th>
                  <th className={styles.th}>Balance Due</th>
                  <th className={styles.th}>Country</th>
                  <th className={styles.th}>State</th>
                  <th className={styles.th}>Region</th>
                  <th className={styles.th}>Overdue Days</th>
                  <th className={styles.th}>Payment Terms</th>
                  <th className={styles.th}>Supply Date</th>
                  <th className={styles.th}>Sales Person</th>
                  <th className={styles.th}>Master Sales Person</th>
                  <th className={styles.th}>Payment Status</th>
                </tr>
              </thead>
              <tbody>
                {displayBalances.length > 0 ? (
                  displayBalances.map((b, index) => (
                    <tr key={`${b.invoiceNo}-${index}`} className={styles.tr}>
                      <td className={styles.td}>{b.invoiceNo}</td>
                      <td className={styles.td}>{formatDate(b.invoiceDate)}</td>
                      <td className={styles.td}>{b.vendorRefNo || 'N/A'}</td>
                      <td className={styles.td}>{b.vendorCode}</td>
                      <td className={styles.td}>{b.vendorName}</td>
                      <td className={styles.td}>{b.contactPerson || 'N/A'}</td>
                      <td className={styles.td}>{formatCurrency(b.invoiceTotal)}</td>
                      <td className={styles.td}>
                        <span className={`${styles.balanceDue} ${b.balanceDue > 0 ? styles.balanceDueOverdue : styles.balanceDueNormal}`}>
                          {formatCurrency(b.balanceDue)}
                        </span>
                      </td>
                      <td className={styles.td}>{b.country}</td>
                      <td className={styles.td}>{b.state}</td>
                      <td className={styles.td}>{b.region || 'Unknown'}</td>
                      <td className={styles.td}>
                        <span className={`${styles.overdueBadge} ${getOverdueBadgeClass(b.overdueDays)}`}>
                          {b.overdueDays} days
                        </span>
                      </td>
                      <td className={styles.td}>{b.paymentTerms}</td>
                      <td className={styles.td}>{formatDate(b.supplyDate)}</td>
                      <td className={styles.td}>{b.salesPerson || 'N/A'}</td>
                      <td className={styles.td}>{b.masterSalesPerson || 'N/A'}</td>
                      <td className={styles.td}>
                        <span className={`${styles.paymentStatus} ${b.paymentStatus === 'Received' ? styles.paymentStatusReceived : styles.paymentStatusPending}`}>
                          {b.paymentStatus}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className={styles.td} colSpan="17" style={{ textAlign: 'center', padding: '40px' }}>
                      {searchTerm || selectedAging ? 'No records match your filters' : 'No records found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && filteredBalances.length > 0 && (
        <div className={styles.paginationSection}>
          <div className={styles.paginationInfo}>
            Showing {currentPage * itemsPerPage + 1} - {Math.min((currentPage + 1) * itemsPerPage, filteredBalances.length)} of {filteredBalances.length} records
          </div>
          <div className={styles.paginationControls}>
            <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0} className={styles.paginationButton}>First</button>
            <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} className={styles.paginationButton}>Prev</button>
            <div className={styles.pageNumber}>{currentPage + 1} / {totalPages}</div>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage === totalPages - 1} className={styles.paginationButton}>Next</button>
            <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage === totalPages - 1} className={styles.paginationButton}>Last</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorBalanceTable;